import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { MessageCircle, ShieldAlert, Lock } from "lucide-react";

const timeLabel = (iso) => {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString([], { day: "2-digit", month: "short" })} · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return ""; }
};

const AdminConversation = ({ parcelId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    adminApi.get(`/chat/parcels/${parcelId}`).then((r) => {
      if (alive) setData(r.data);
    }).catch(() => {
      if (alive) setData({ exists: false, messages: [], pii_flags: [], message_count: 0 });
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [parcelId]);

  if (loading) return null;
  if (!data?.exists) return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 mt-6" data-testid="admin-chat-empty">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-2">
        <MessageCircle className="w-4 h-4"/> Conversation
      </div>
      <div className="text-xs text-slate-400 italic mt-2">No delivery on this parcel yet.</div>
    </div>
  );

  const flagCount = (data.pii_flags || []).length;
  const hdr = data.header || {};

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 mt-6" data-testid="admin-chat-section">
      <div className="flex items-center gap-2 mb-3">
        <MessageCircle className="w-4 h-4 text-slate-500"/>
        <h2 className="font-display font-semibold text-slate-900">Conversation</h2>
        <span className="text-[11px] text-slate-500">
          {data.message_count} message{data.message_count === 1 ? "" : "s"}
        </span>
        {flagCount > 0 && (
          <span
            data-testid="admin-chat-pii-count"
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium rounded-full bg-amber-100 text-amber-800 px-2 py-0.5"
            title="Contact-detail sharing attempts"
          >
            <ShieldAlert className="w-3 h-3"/> {flagCount} PII attempt{flagCount === 1 ? "" : "s"}
          </span>
        )}
        <span className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 ml-2 inline-flex items-center gap-1">
          <Lock className="w-3 h-3"/> {hdr.chat_status || "—"}
        </span>
      </div>

      <div className="max-h-96 overflow-y-auto space-y-2 bg-slate-50 rounded-xl p-3">
        {(data.messages || []).length === 0 && (
          <div className="text-center text-xs text-slate-400 py-6">No messages exchanged.</div>
        )}
        {(data.messages || []).map((m) => (
          <div key={m.id} className={`flex ${m.role === "sender" ? "justify-start" : "justify-end"}`}>
            <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.role === "sender" ? "bg-white border border-slate-200" : "bg-slate-900 text-white"}`}>
              <div className={`text-[10px] uppercase tracking-wider mb-0.5 ${m.role === "sender" ? "text-slate-500" : "text-white/60"}`}>
                {m.role} · {m.from_name || ""}
              </div>
              <div className="whitespace-pre-wrap break-words" data-testid={`admin-chat-msg-${m.id}`}>{m.text}</div>
              <div className={`text-[10px] mt-1 ${m.role === "sender" ? "text-slate-400" : "text-white/50"}`}>
                {m.masked && <span className="mr-1 uppercase tracking-wider">hidden contact ·</span>}
                {timeLabel(m.created_at)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {flagCount > 0 && (
        <details className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="admin-pii-log">
          <summary className="text-xs font-medium text-amber-900 cursor-pointer inline-flex items-center gap-1">
            <ShieldAlert className="w-3 h-3"/> Contact-detail sharing attempts ({flagCount})
          </summary>
          <ul className="mt-2 space-y-1 text-xs text-amber-900/80">
            {data.pii_flags.map((f) => (
              <li key={f.id} className="flex items-start gap-2">
                <span className="text-slate-500 shrink-0">{timeLabel(f.created_at)}</span>
                <span className="uppercase tracking-wider text-[10px] font-medium bg-amber-100 px-1.5 py-0.5 rounded">{f.user_role}</span>
                <span className="truncate">{f.matches?.map((mm) => mm.kind).join(", ")}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default AdminConversation;
